import { Link } from "react-router-dom"
import { contactPath } from "~/common/paths"
import { FounderForm } from "~/marketing/lead-forms"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/ui/accordion"

const lessons = [
  [
    "Defining your goals and aligning them with your business strategy",
    "Start with the problem your product solves and the people who need it most. We’ll help you connect that need to your business goals, work out how you’ll reach customers, and think through how the business will make money.",
  ],
  [
    "Understanding your market: The key to meeting user needs",
    "Find out whether people need what you’re planning to build. We’ll walk through customer interviews, simple landing pages, and clickable prototypes you can use to test your idea, learn from potential users, and find a focused place to start.",
  ],
  [
    "The role of a tech-savvy team in your success",
    "Know what to look for in a technical partner, whether you’re hiring or working with an agency. We’ll cover the questions to ask, the warning signs to watch for, and how to find people who understand your business and communicate clearly.",
  ],
  [
    "Planning, estimating and budgeting: Setting yourself up for success",
    "Turn your idea into a plan your team can estimate. We’ll help you define the first version, map out how people will use it, and budget for the build and what comes after. Includes our requirements and roadmap templates, plus a real project estimate.",
  ],
  [
    "Understanding tech proposals: Deciphering jargon and quotes",
    "Understand what you’re actually buying before you choose a team. We’ll show you how to check a proposal against your requirements, spot missing costs, and ask about communication, staffing, and scope changes. Includes questions to use when checking a vendor’s references.",
  ],
  [
    "Building the coalition: Who should be on your team, what role you should play, and what's the day-to-day?",
    "Get clear on who needs to be involved and what your role looks like once the build starts. We’ll cover decisions, regular check-ins, and demos, with templates to help you assign responsibilities and keep track of progress and budget.",
  ],
  [
    "Beyond development: Growing and evolving your tech product",
    "Use what you learn after launch to decide what comes next. We’ll cover gathering feedback, testing ways to reach customers, and balancing new features with maintenance and cash flow. Includes tools for tracking product usage and deciding which technical issues to tackle.",
  ],
] as const

export const FirstTimeFoundersPage = () => (
  <div className="service-page founder-page">
    <section className="service-section">
      <div className="service-container founder-hero-grid">
        <div className="founder-introduction">
          <h1 className="marketing-page-title marketing-split-title">
            <span className="headline-beat">
              First Time <span className="home-emphasis home-highlight">Founders</span>
            </span>{" "}
            <span className="headline-beat">Email Series</span>
          </h1>
          <p className="service-intro">
            This 7 part email series packages everything we&apos;ve learned building over 200 tech
            products, mainly from first time founders. We&apos;ve also included product roadmap
            templates, vendor evaluation checklists, and many more resources.
          </p>
        </div>
        <div id="founder-signup" className="founder-signup" tabIndex={-1}>
          <h2>
            Thinking about starting a tech business?
            <br />
            We&apos;ve created a free email-based course to guide you!
          </h2>
          <FounderForm />
        </div>
      </div>
    </section>
    <section className="service-section service-brown" aria-label="Email series lessons">
      <div className="service-container founder-course-grid">
        <div>
          <div className="founder-course-intro">
            <h2>Intro: Starting your tech journey: Key considerations</h2>
            <p>
              We’ll guide you through the key skills you need to successfully build the software
              that powers your business, whether you choose to use an outside agency or hire and
              build your own team. These skills are universally useful and we’ll be sharing
              case-studies, document templates, and open-book financials from some of the 150+
              founders we’ve worked with over the last decade.
            </p>
          </div>
          <Accordion type="multiple" className="founder-lessons">
            {lessons.map(([title, description], index) => (
              <AccordionItem key={title} value={`email-${index + 1}`} className="founder-lesson">
                <AccordionTrigger className="founder-lesson-trigger">
                  <span>
                    Email {index + 1}: {title}
                  </span>
                  <span aria-hidden="true" className="founder-toggle" />
                </AccordionTrigger>
                <AccordionContent className="founder-lesson-description">
                  {description}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
        <aside className="founder-course-aside">
          <h2>
            7 Part
            <br />
            Email Series
          </h2>
          <p>Sign up today to get the entire email series delivered directly to your inbox.</p>
          <a className="service-cta founder-cta" href="#founder-signup">
            Subscribe Today
          </a>
          <img
            className="founder-teacher"
            src="/images/founders/teacher-tanooki.png"
            alt=""
            width={1280}
            height={1280}
            loading="lazy"
          />
        </aside>
      </div>
    </section>
    <section className="service-section">
      <div className="service-container founder-contact">
        <h2>
          Let&apos;s talk
          <br />
          about how we can help.
        </h2>
        <p className="service-intro">
          We LOVE talking about ideas and how to apply tech to bring them to life. Whether
          you&apos;re ready to build tomorrow or exploring, we’ll share our thoughts and help
          however we can.
        </p>
        <Link className="service-cta founder-cta" to={contactPath({})}>
          Get in Touch
        </Link>
      </div>
    </section>
  </div>
)
